import { definePreset, palette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import type { ToggleSwitchDesignTokens } from '@primeuix/themes/types/toggleswitch';
import type { AccordionDesignTokens } from '@primeuix/themes/types/accordion';
import type { MultiSelectDesignTokens } from '@primeuix/themes/types/multiselect';
import type { CheckboxDesignTokens } from '@primeuix/themes/types/checkbox';
import type { RadioButtonDesignTokens } from '@primeuix/themes/types/radiobutton';
import type { TooltipDesignTokens } from '@primeuix/themes/types/tooltip';
import type { SelectDesignTokens } from '@primeuix/themes/types/select';

//#region    ToggleSwitch

const tsRoot: ToggleSwitchDesignTokens['root'] = {
  width: '3rem',
  height: '1.75rem',
  borderRadius: '30px',
  gap: '0.25rem',
  shadow: 'none',
  borderWidth: '1px',
  borderColor: '#98A2B3',
  hoverBorderColor: '#98A2B3',
  checkedBorderColor: '#98A2B3',
  checkedHoverBorderColor: '#98A2B3',
  invalidBorderColor: '{form.field.invalid.border.color}',
  transitionDuration: '0.2s',
  slideDuration: '0.2s',
  focusRing: {
    width: '{focus.ring.width}',
    style: '{focus.ring.style}',
    color: '{focus.ring.color}',
    offset: '{focus.ring.offset}',
    shadow: 'none',
  },
};

const tsHandle: ToggleSwitchDesignTokens['handle'] = {
  borderRadius: '50%',
  size: '1.25rem',
  background: '#98A2B3',
  hoverBackground: '#98A2B3',
  checkedBackground: '#0E9E73',
  checkedHoverBackground: '#0E9E73',
  disabledBackground: '#98A2B3',
};

const tsColorScheme: ToggleSwitchDesignTokens['colorScheme'] = {
  light: {
    root: {
      background: 'transparent',
      disabledBackground: 'transparent',
      hoverBackground: 'transparent',
      checkedBackground: 'transparent',
      checkedHoverBackground: 'transparent',
    },
    handle: { ...tsHandle },
  },
};
//#endregion
//#region Accordion
const accordionTokens: AccordionDesignTokens = {
  panel: {
    borderWidth: '0 0 0 0',
  },
  header: {
    padding: '0.75rem 1rem 0.75rem 1rem',
    borderRadius: '0.5rem',
    borderWidth: '0',
    borderColor: 'transparent',
    background: '#f8f9fa',
    hoverBackground: '#f8f9fa',
    activeBackground: '#f8f9fa',
    activeHoverBackground: '#f8f9fa',
    toggleIcon: {
      color: '#101828',
      hoverColor: '#101828',
      activeColor: '#101828',
      activeHoverColor: '#101828',
    },
  },
  content: {
    borderWidth: '0',
    borderColor: '{content.border.color}',
    background: '#f8f9fa',
    color: '{text.color}',
    padding: '1rem 0.875rem 1rem 0.875rem',
  },
};

//#endregion
//#region MultiSelect
const msRoot: MultiSelectDesignTokens['root'] = {
  borderRadius: '8px',
  borderColor: '#D0D5DD',
  hoverBorderColor: '#9fd8c7',
  focusBorderColor: '#9fd8c7',
  shadow: 'none',
  paddingX: '0.75rem',
  paddingY: '0.375rem',
  focusRing: {
    width: '2px',
    style: 'solid',
    color: 'rgba(110,197,171,0.15)',
    offset: '0',
    shadow: 'none',
  },
};

const msOverlay: MultiSelectDesignTokens['overlay'] = {
  borderRadius: '8px',
  borderColor: '#EAECF0',
  shadow: 'none',
  background: '#ffffff',
};

const msList: MultiSelectDesignTokens['list'] = {
  padding: '0',
  header: { padding: '12px 1rem' },
};

const msOption: MultiSelectDesignTokens['option'] = {
  padding: '1rem',
  color: '#101828',
  selectedBackground: 'transparent',
  selectedFocusBackground: 'transparent',
  focusBackground: '#ffffff',
  borderRadius: '0',
};

const msChip: MultiSelectDesignTokens['chip'] = {
  borderRadius: '4px',
};

const msClearIcon: MultiSelectDesignTokens['clearIcon'] = {
  color: '#667085',
};

const multiSelectTokens: MultiSelectDesignTokens = {
  root: msRoot,
  overlay: msOverlay,
  list: msList,
  option: msOption,
  chip: msChip,
  clearIcon: msClearIcon,
};

//#endregion
//#region Checkbox
const checkboxTokens: CheckboxDesignTokens = {
  root: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    // borderWidth: '2px',
    borderColor: '#d0d5dd',
    hoverBorderColor: '#0e9e73',
    focusBorderColor: '#d0d5dd',
    background: '#ffffff',

    checkedBackground: '#0e9e73',
    checkedHoverBackground: '#0e9e73',
    checkedBorderColor: '#0e9e73',
    checkedHoverBorderColor: '#0e9e73',

    shadow: 'none',
    transitionDuration: '0.2s',
  },

  icon: {
    size: '14px',
    color: 'transparent',
    checkedColor: '#ffffff',
    checkedHoverColor: '#ffffff',
  },
};

//#endregion
//#region RadioButton
const radioButtonTokens: RadioButtonDesignTokens = {
  root: {
    width: '20px',
    height: '20px',
    borderColor: '#d0d5dd',
    hoverBorderColor: '#0e9e73',
    focusBorderColor: '#d0d5dd',
    background: '#ffffff',

    checkedBackground: '#0e9e73',
    checkedHoverBackground: '#0e9e73',
    checkedBorderColor: '#0e9e73',
    checkedHoverBorderColor: '#0e9e73',

    shadow: 'none',
    transitionDuration: '0.2s',
  },

  icon: {
    size: '8px',
    checkedColor: '#ffffff',
    checkedHoverColor: '#ffffff',
  },
};

//#endregion
//#region Tooltip
const tooltipTokens: TooltipDesignTokens = {
  root: {
    maxWidth: 'none',
    gutter: '0.25rem',
    padding: '12px',
    borderRadius: '8px',
    background: '#FFFFFF',
    color: '#101828',
    shadow: '0px 32px 64px -12px rgba(16, 24, 40, 0.1)',
  },
  colorScheme: {
    light: {
      root: {
        background: '#FFFFFF',
        color: '#101828',
      },
    },
    dark: {
      root: {
        background: '#FFFFFF',
        color: '#101828',
      },
    },
  },
  css: `
.p-tooltip .p-tooltip-text {
  background: #FFFFFF;
  color: #101828;
  border: 1px solid #D0D5DD;
  font-size: 0.875rem;
  line-height: 1.28571429;
  font-weight: 400;
  white-space: nowrap;
  word-break: keep-all;
  overflow-wrap: normal;
}
.p-tooltip.sb-tooltip-multiline,
.sb-tooltip-multiline {
  max-width: 340px;
}
.p-tooltip.sb-tooltip-multiline .p-tooltip-text,
.sb-tooltip-multiline .p-tooltip-text {
  white-space: pre-line;
  word-break: normal;
  overflow-wrap: anywhere;
}

.p-tooltip .p-tooltip-arrow {
  border-color: transparent !important;
}

.p-tooltip.p-tooltip-top .p-tooltip-arrow {
  border-top-color: #D0D5DD !important;
}

.p-tooltip.p-tooltip-right .p-tooltip-arrow {
  border-right-color: #D0D5DD !important;
}

.p-tooltip.p-tooltip-bottom .p-tooltip-arrow {
  border-bottom-color: #D0D5DD !important;
}

.p-tooltip.p-tooltip-left .p-tooltip-arrow {
  border-left-color: #D0D5DD !important;
}
`,
};

//#endregion
//#region Select
const selectTokens: SelectDesignTokens = {
  option: {
    selectedColor: '#101828',
    selectedBackground: '#E7F5F1',
    selectedFocusColor: '#101828',
    selectedFocusBackground: '#E7F5F1',
  },
};
//#endregion

const MySBtheme = definePreset(Aura, {
  semantic: {
    // primary: palette('#1f3c76')
  },
  components: {
    toggleswitch: {
      root: tsRoot,
      handle: tsHandle,
      colorScheme: tsColorScheme,
    } satisfies ToggleSwitchDesignTokens,
    accordion: accordionTokens satisfies AccordionDesignTokens,
    checkbox: checkboxTokens,
    radiobutton: radioButtonTokens,
    tooltip: tooltipTokens,
    select: selectTokens satisfies SelectDesignTokens,
  },
});

export default MySBtheme;
